// Our custom MUI theme (ref - https://material-ui.com/customization/themes/#other-variables)
import createMuiTheme, { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';

// NOTE: When adding or deleting colors in this file be sure to keep the CustomAppColors interface
// src/interfaces/themeInterfaces.ts in sync.
export const trimbleColors = {
    appHeaderColor: '#00437b',
    appPrimaryColor: '#002D5B',
    appPrimaryIconColor: '#005f9e',
    appSecondaryColor: '#FFBE00',
    primaryTextColor: '#363545',
    subHeaderTextColor: '#7b7a86',
    appMenuBackgroundColor: '#f3f3f7',
    appMenuSelectedBackgroundColor: '#0063a3',
    appMenuSelectedBackgroundHoverColor: '#217cbb',
    appMenuHoverBackgroundColor: '#e0e1e9',
    inputPlaceholderColor: '#d8dee9',
    formRootLightColor: '#F3F3F7',
    formLabelLightColor: '#009ad9',
    inputNonFocusedLightColor: '#00437b',
    selectedTabColor: '#009ad9',
    shadedTableBackgroundColor: '#e2e2e7',

    // trending colors
    trendingUpColor: '#5aa700',
    trendingDownColor: '#ff8b00',
    trendingFlatColor: '#cbaa39',
    trimbleGreen: '#349C44',

    // all in rate colors
    baseRateColor: '#0086f6',
    accessorialChargesColor: '#32c8fc',
    fuelSurchargeColor: '#6bdffd',

    // transit colors
    standardTransitColor: '#02b6fb',
    delayColor: '#ffba43',
    unloadColor: '#00a1af',
    loadColor: '#00727c',
    emptyDriveColor: '#ff8324',

    // rate fluctuation colors
    rateHigh: '#4ec98c',
    rateAverage: '#078af6',
    rateLow: '#ffc552',

    // seasonality trending colors
    seasonalityTrendingUpColor: '#7ed321',
    seasonalityTrendingDownColor: '#ff511a',
    seasonalityTrendingFlatColor: '#ff8b00',

    // seasonality trending text colors
    seasonalityTrendingUpTextColor: '#000',
    seasonalityTrendingDownTextColor: '#fff',
    seasonalityTrendingFlatTextColor: '#000',

    // grade trending colors
    gradeAColor: '#40cb8d',
    gradeBColor: '#7ed321',
    gradeCColor: '#c9c94f',
    gradeDColor: '#f0844f',

    dataChartsBorderColor: '#a8a8a8',
    dataChartsBackgroundColor: '#f3f3f7',

    // data charts - national chart colors
    dataChartsNationalAvgRateColor: '#009ad9',
    dataChartsNationalAvgLineHaulRateColor: '#7ed321',
    dataChartsNationalFuelSurchargeColor: '#ffbe00',

    // data charts - state chart colors
    dataChartsStateInboundColor: '#ff511a',
    dataChartsStateOutboundColor: '#009ad9',

    // data charts - all state chart colors
    dataChartsAllStatesInboundColor: '#FBD4D7',
    dataChartsAllStatesOutboundColor: '#DCEDF9',

    paginationColor: '#fafafa',

    // Trimble Neutrals
    neutrals: {
        white: '#ffffff',
        grayHalf: '#f9f9fb',
        grayLight: '#F3F3F7',
        gray0: '#EAEAEF',
        gray1: '#E2E2E7',
        gray2: '#D0D0D7',
        gray3: '#BFBFC6',
        gray4: '#AEAEB6',
        gray5: '#9D9DA6',
        gray6: '#8C8B96',
        gray7: '#7B7A86',
        gray8: '#6A6976',
        gray9: '#595868',
        gray10: '#474655',
        trimbleGray: '#363545',
        grayDark1: '#262533',
        grayDark2: '#1B1A26',
        grayDark3: '#0B0A12',
        grayBlack: '#0'

    },
    otherColors: {
        bewitchingBlue: '#BBD0E3',
        errorRed: '#DA212C',
        paleGreen: '#E0ECCF'
    }
};

const themeOptions: ThemeOptions = {
    typography: {
        fontFamily: '"Open Sans", Roboto, Helvetica, Arial, sans-serif'
    },
    appColors: {
        // Add on extra Trimble app specific colors
        ...trimbleColors
    },
    palette: {
        type: 'light',
        primary: {
            main: trimbleColors.appPrimaryColor
        },
        secondary: {
            main: trimbleColors.appSecondaryColor
        },
        text: {
            primary: trimbleColors.primaryTextColor
        }
    },
    overrides: {
        MuiToolbar: {
            root: {
                backgroundColor: `${trimbleColors.appHeaderColor} !important`
            }
        },
        MuiFormControl: {
            root: {
                backgroundColor: '#fff'
            }
        },
        MuiInput: {
            underline: {
                '&:before': {
                    borderBottom: `1px solid ${trimbleColors.inputNonFocusedLightColor}`
                },
                '&:hover:not(.Mui-disabled):before': {
                    borderBottom: `2px solid ${trimbleColors.inputNonFocusedLightColor}`
                },
                '&$focused': {
                    borderBottom: `1px solid ${trimbleColors.appSecondaryColor}`
                }
            }
        },
        MuiFilledInput: {
            underline: {
                '&:before': {
                    borderBottom: `1px solid ${trimbleColors.inputNonFocusedLightColor}`
                },
                '&:hover:not(.Mui-disabled):before': {
                    borderBottom: `2px solid ${trimbleColors.inputNonFocusedLightColor}`
                },
                '&$focused': {
                    borderBottom: `1px solid ${trimbleColors.appPrimaryIconColor}`
                },
                '&:after': {
                    borderBottom: `1px solid ${trimbleColors.appPrimaryIconColor}`
                }
            },
            input: {
                color: '#000',
                fontSize: '16px',
                fontWeight: 600
            }
        },
        MuiInputLabel: {
            root: {
                color: trimbleColors.inputNonFocusedLightColor,
                '&$focused': {
                    color: trimbleColors.appPrimaryIconColor
                }
            }
        },
        MuiButton: {
            root: {
                fontWeight: 600
            },
            contained: {
                '&$disabled': {
                    backgroundColor: trimbleColors.appSecondaryColor,
                    color: trimbleColors.appPrimaryColor,
                    opacity: 0.6
                }
            },
            containedSecondary: {
                color: trimbleColors.appPrimaryColor
            }
        },
        MuiCircularProgress: {
            colorPrimary: {
                color: trimbleColors.appPrimaryIconColor
            },
            colorSecondary: {
                color: '#ffffff'
            }
        },
        MuiTabs: {
            indicator: {
                backgroundColor: trimbleColors.selectedTabColor
            }
        },
        MuiTab: {
            root: {
                borderBottom: '1px solid #a6b0c5'
            },
            textColorPrimary: {
                fontSize: '14px',
                color: trimbleColors.appHeaderColor,
                fontWeight: 600,
                '&$selected': {
                    color: trimbleColors.selectedTabColor,
                    fontWeight: 700
                }
            }
        },
        MuiTableRow: {
            hover: {
                cursor: 'pointer'
            }
        },
        MuiTableCell: {
            head: {
                color: trimbleColors.subHeaderTextColor,
                fontSize: '11px',
                fontWeight: 600
            },
            body: {
                fontSize: '14px'
            }
        },
        MuiFormControlLabel: {
            root: {
                marginRight: '0px'
            },
            label: {
                fontSize: '12px'
            }
        },
        MuiListItem: {
            root: {
                color: trimbleColors.appHeaderColor,
                fontWeight: 600,
                '&$selected': {
                    backgroundColor: trimbleColors.dataChartsBackgroundColor,
                    color: trimbleColors.selectedTabColor,
                    fontWeight: 700
                }
            }
        },
        MuiListItemIcon: {
            root: {
                color: 'inherit'
            }
        },
        MuiListItemText: {
            primary: {
                fontSize: '14px',
                textTransform: 'uppercase',
                fontWeight: 'inherit'
            }
        }
    }
};

// More on creating a Custom Theme: https://material-ui.com/guides/typescript/#customization-of-theme
const mui104Theme = createMuiTheme(themeOptions);

export default mui104Theme;
